/**
 * Meta (Facebook) Pixel loader + PageView tracker.
 *
 * Injects the standard Meta Pixel base code, initializes the pixel
 * with the configured ID, and fires a single "PageView" event.
 *
 * IMPORTANT: The published Magic Patterns site is served at
 * `project-<slug>.magicpatterns.app` as a TOP-LEVEL document, so the pixel
 * MUST load there for Meta Pixel Helper / Events Manager to detect it.
 * We only skip loading inside the EDITOR CANVAS (an iframe), where a
 * cross-origin third-party script can surface as an opaque "Script error.".
 */

export const META_PIXEL_ID = '1904779843432668'

declare global {
  interface Window {
    fbq?: any
    _fbq?: any
  }
}

let initialized = false

/**
 * True only when we are rendered inside the Magic Patterns editor canvas
 * (i.e. nested in an iframe). The published site runs as the top window,
 * so this is false there and the pixel loads normally.
 */
function isEditorCanvas(): boolean {
  try {
    if (typeof window === 'undefined') return true
    return window.self !== window.top
  } catch {
    // Accessing window.top across origins throws -> we are in a nested frame.
    return true
  }
}

export function initMetaPixel(pixelId: string = META_PIXEL_ID): void {
  if (typeof window === 'undefined') return

  // Only skip inside the editor canvas iframe — load everywhere else,
  // including the published *.magicpatterns.app site and custom domains.
  if (isEditorCanvas()) return

  try {
    if (initialized) {
      window.fbq?.('track', 'PageView')
      return
    }

    /* eslint-disable */
    ;(function (f: any, b: Document, e: string, v: string) {
      if (f.fbq) return
      const n: any = (f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments)
      })
      if (!f._fbq) f._fbq = n
      n.push = n
      n.loaded = true
      n.version = '2.0'
      n.queue = []
      const t = b.createElement(e) as HTMLScriptElement
      t.async = true
      t.src = v
      // Swallow load failures so a blocked/failed script never bubbles up.
      t.onerror = () => {}
      const s = b.getElementsByTagName(e)[0]
      s.parentNode?.insertBefore(t, s)
    })(
      window,
      document,
      'script',
      'https://connect.facebook.net/en_US/fbevents.js',
    )
    /* eslint-enable */

    window.fbq('init', pixelId)
    window.fbq('track', 'PageView')

    initialized = true
  } catch {
    // Any failure here must not break the app.
  }
}
